import React, { useCallback, useState } from 'react';
import { Dialog, DialogBody, DialogHeader, Icon } from '@repo/ui';
import { AppealStatus } from '@repo/types';

import type { Appeal, MatterInvoiceSummaryFile } from '../../types';
import { useGetAppealList, useGetInvoice, useMatterInvoiceSummaryFilesQuery } from '../../queries';
import { AppealResolutionForm } from '../common/appeal-denied';

interface AppealResolutionModalProps {
  invoiceId: string;
  isOpen: boolean;
  isLoading?: boolean;
  basicInformation?: Partial<Appeal>;
  maxAmount?: number;
  title?: string;
  warningText?: string;
  isCommentRequired?: boolean;
  isFilesRequired?: boolean;
  targetStatus: AppealStatus;
  onClose: () => void;
  onSubmit: (payload: {
    comment: string;
    files: File[];
    selectedExistingFiles: MatterInvoiceSummaryFile[];
    targetStatus: AppealStatus;
  }) => Promise<void>;
}

/**
 * Modal for Appeal Resolution (Appeal Denied & Deadline Expired).
 * Supports optional validation for comment and attachments.
 */
export const AppealResolutionModal: React.FC<AppealResolutionModalProps> = ({
  invoiceId,
  isOpen,
  isLoading = false,
  basicInformation,
  maxAmount,
  title,
  warningText,
  isCommentRequired = true,
  isFilesRequired = true,
  targetStatus,
  onClose,
  onSubmit,
}) => {
  const { data: appeals, isLoading: isLoadingAppeals } = useGetAppealList(invoiceId || '', {
    enabled: !!invoiceId && isOpen,
  });
  const { data: invoice } = useGetInvoice(invoiceId || '', {
    enabled: !!invoiceId && isOpen,
  });

  const currentAppeal = appeals?.find(
    appeal => appeal.appealStatus === AppealStatus.APPEAL_SUBMITTED
  );
  const {
    data: existingFilesResponse,
    isFetching: isFetchingExistingFiles,
    isLoading: isLoadingExistingFiles,
  } = useMatterInvoiceSummaryFilesQuery(invoiceId, { enabled: isOpen });

  const existingFiles = existingFilesResponse?.data || [];

  const [files, setFiles] = useState<File[]>([]);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<{ comment: string; files: string }>({
    comment: '',
    files: '',
  });
  const [selectedExistingFiles, setSelectedExistingFiles] = useState<MatterInvoiceSummaryFile[]>(
    []
  );

  const handleClose = useCallback(() => {
    setFiles([]);
    setComment('');
    setErrors({ comment: '', files: '' });
    setSelectedExistingFiles([]);
    onClose();
  }, [onClose]);

  const handleInputChange = useCallback((field: 'comment', value: string) => {
    if (field === 'comment') {
      setComment(value);
      setErrors(prev => ({ ...prev, comment: '' }));
    }
  }, []);

  const handleSetError = useCallback((field: 'comment' | 'files', error: string) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  }, []);

  const handleFilesChange = useCallback(
    (nextFiles: File[]) => {
      setFiles(nextFiles);
      const hasFiles = nextFiles.length > 0 || selectedExistingFiles.length > 0;
      setErrors(prev => ({
        ...prev,
        files: hasFiles ? '' : prev.files,
      }));
    },
    [selectedExistingFiles.length]
  );

  const handleExistingFilesChange = useCallback((files: MatterInvoiceSummaryFile[]) => {
    setSelectedExistingFiles(files);
    const hasFiles = files.length > 0;
    setErrors(prev => ({
      ...prev,
      files: hasFiles ? '' : prev.files,
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    await onSubmit({ comment, files, selectedExistingFiles, targetStatus });
    setFiles([]);
    setComment('');
  }, [comment, files, onSubmit, selectedExistingFiles, targetStatus]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="xxllg"
      showCloseButton={false}
      className="!max-h-[90vh] overflow-y-auto"
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">{title}</h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={handleClose}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-4">
        <AppealResolutionForm
          matterDisplayNumber={basicInformation?.matterDisplayNumber ?? ''}
          responsibleAttorneyName={basicInformation?.responsibleAttorneyName ?? ''}
          clientName={basicInformation?.clientName ?? ''}
          invoiceDate={basicInformation?.invoiceDate ?? ''}
          invoiceNumber={basicInformation?.invoiceNumber ?? ''}
          outstandingAmount={basicInformation?.amountDue ?? 0}
          amountPaid={basicInformation?.paid ?? 0}
          totalAmount={basicInformation?.totalAmount ?? 0}
          creditsIssued={basicInformation?.creditsIssued ?? 0}
          amountToWriteOff={
            currentAppeal
              ? (maxAmount ?? currentAppeal?.remainingAmount ?? 0)
              : (invoice?.outstandingBalance ?? 0)
          }
          feesCut={currentAppeal ? currentAppeal?.feesCut : invoice?.outstandingFees}
          expensesCut={currentAppeal ? currentAppeal?.expensesCut : invoice?.outstandingExpenses}
          comment={comment}
          files={files}
          existingFiles={existingFiles}
          errors={errors}
          isLoading={isLoading || isLoadingAppeals}
          warningText={warningText}
          isCommentRequired={isCommentRequired}
          isFilesRequired={isFilesRequired}
          onInputChange={handleInputChange}
          onFilesChange={handleFilesChange}
          onExistingFilesChange={handleExistingFilesChange}
          onSubmit={handleSubmit}
          setError={handleSetError}
          selectedExistingFiles={selectedExistingFiles}
        />
      </DialogBody>
    </Dialog>
  );
};

export default AppealResolutionModal;
